import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  CircularProgress,
  Grid,
  Chip,
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Breadcrumbs,
  IconButton,
  Backdrop,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import FolderIcon from "@mui/icons-material/Folder";
import CollectionListItem from "./collectionListItemV2";
import CollectionListView from "./collectionListView";
import CollectionsGroupSendButton from "./collectionGroupSendButton";
import ControlBar from "../dashboard/controlBar";
import NewFolderDialog from "./newFolderDialog";
import { FolderThumbnail } from "./collectionFolderItem";
import { Link } from "react-router-dom";
import HelpHint from "../parts/helpHint";
import { Distribute } from "../parts/methods/distribute";
import { toast } from "react-toastify";
import Moment from "moment";
import { CollectionsApi, EmailsApi, UsersApi } from "../../services/api/api";

import CollectionFolderMoveDialog from "./collectionFolderMoveDialog";
import CollectionAccessDialog from "./CollectionAccessDialog";
import { useCognitoAuth } from "../../aws-amplify-auth";

Moment.locale("en");

function buildLevel(collections, emptyFolders, currentPath) {
  // Combine all known folder paths from collections + empty folders
  const allFolderPaths = new Set();

  // Add paths from collections
  collections.forEach((c) => {
    if (Array.isArray(c.folderPath) && c.folderPath.length > 0) {
      allFolderPaths.add(JSON.stringify(c.folderPath));
    }
  });

  // Add empty folder paths from localStorage
  emptyFolders.forEach((ef) => {
    allFolderPaths.add(JSON.stringify(ef));
  });

  // Find subfolders: distinct next-segment names where prefix matches currentPath
  const subfolderNames = new Set();
  allFolderPaths.forEach((pathStr) => {
    const path = JSON.parse(pathStr);
    // Check if path starts with currentPath and has at least one more segment
    if (path.length > currentPath.length) {
      const matchesPrefix = currentPath.every((seg, i) => path[i] === seg);
      if (matchesPrefix) {
        subfolderNames.add(path[currentPath.length]);
      }
    }
  });

  // Collections at this level: folderPath deep-equals currentPath
  const collectionsAtLevel = collections.filter((c) => {
    const fp = Array.isArray(c.folderPath) ? c.folderPath : [];
    return fp.length === currentPath.length &&
           fp.every((seg, i) => seg === currentPath[i]);
  });

  // Count items in each subfolder (for display)
  const subfolders = Array.from(subfolderNames).sort().map((name) => {
    const subPath = [...currentPath, name];
    const count = collections.filter((c) => {
      const fp = Array.isArray(c.folderPath) ? c.folderPath : [];
      return fp.length >= subPath.length &&
             fp.slice(0, subPath.length).every((seg, i) => seg === subPath[i]);
    }).length;
    return { name, path: subPath, count };
  });

  return { subfolders, collectionsAtLevel };
}

function loadEmptyFolders(userSub) {
  try {
    const stored = localStorage.getItem(`shieldshot_empty_folders_${userSub || 'unknown'}`);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
}

function saveEmptyFolders(userSub, folders) {
  try {
    localStorage.setItem(`shieldshot_empty_folders_${userSub || 'unknown'}`, JSON.stringify(folders));
  } catch {}
}

function Collections(props) {
  const { refreshUserProfile } = useCognitoAuth();
  const [collections, setCollections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [empty, setEmpty] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupedSets, setGroupedSets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [currentSet, setCurrentSet] = useState("");
  const [currentTitle, setCurrentTitle] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [message, setMessage] = useState("");
  const [emailList, setEmailList] = useState([]);
  const [emails, setEmails] = useState([]);
  const [disableButtons, setDisableButtons] = useState(false);
  const [defaultTemplate, setDefaultTemplate] = useState("");
  const [viewMode, setViewMode] = useState("thumbnail");
  const [groupBy, setGroupBy] = useState("folder");
  const [monthlyEnabled, setMonthlyEnabled] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveCollection, setMoveCollection] = useState(null);
  const [currentPath, setCurrentPath] = useState([]);
  const [emptyFolders, setEmptyFolders] = useState(() => loadEmptyFolders(props.user?.sub));
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [overFolder, setOverFolder] = useState(null);
  const [moveLoading, setMoveLoading] = useState(false);
  const [collectionFilter, setCollectionFilter] = useState("all");
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [selectedAccessCollection, setSelectedAccessCollection] = useState(null);

  const accountType = props.user?.app_metadata?.accountType;
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    window.scrollTo(0, 0);

    // Check for cached data with user-specific keys
    const userCacheKey = `collections_${props.user?.sub || 'unknown'}`;
    const groupsCacheKey = `groupedSets_${props.user?.sub || 'unknown'}`;
    const tiersCacheKey = `collectionTiers_${props.user?.sub || 'unknown'}`;
    const groupsListCacheKey = `groups_${props.user?.sub || 'unknown'}`;

    if (localStorage.getItem(groupsCacheKey) && props.user?.sub) {
      setCollections(JSON.parse(localStorage.getItem(userCacheKey) || '[]'));
      setTiers(JSON.parse(localStorage.getItem(tiersCacheKey) || '[]'));
      setGroups(JSON.parse(localStorage.getItem(groupsListCacheKey) || '[]'));
      setGroupedSets(JSON.parse(localStorage.getItem(groupsCacheKey) || '[]'));
      setIsLoading(false);
    }

    getPhotoListEndpoint();

    const fetchEmailList = async () => {
      try {
        let token = props.token;
        if (typeof token === 'function') {
          token = await token();
        }
        if (!token || typeof token !== 'string' || token === 'undefined') {
          toast.error('No valid token found for email list API call.');
          return;
        }
        
        const result = await EmailsApi.getEmails(token);
        if (result.success) {
          const emailList = result.data.emaillist || [];
          setEmailList(emailList);
        } else {
          if (process.env.NODE_ENV === 'development') console.log("ERROR", result.message);
          toast.error("Error getting emails: " + (result.message || 'Unknown error'));
        }
      } catch (error) {
        console.error("Email list fetch error:", error);
        toast.error("Failed to load email list: " + error.message);
        setEmailList([]); // Ensure it's an empty array on error
      }
    };
    fetchEmailList();

    const fetchDefaultTemplate = async () => {
      try {
        // Check for cached template first
        const templateCacheKey = `defaultTemplate_${props.user?.sub || 'unknown'}`;
        const cachedTemplate = localStorage.getItem(templateCacheKey);
        if (cachedTemplate) {
          setDefaultTemplate(cachedTemplate);
        }

        let token = props.token;
        if (typeof token === 'function') {
          token = await token();
        }
        if (!token || typeof token !== 'string' || token === 'undefined') {
          console.warn('No valid token found for template API call.');
          return;
        }
        
        const response = await UsersApi.getTemplates(token);
        if (response && response.success) {
          let templates = [];
          
          if (response.data && response.data.length > 0 && response.data[0].data) {
            templates = response.data[0].data; // Array of [name, content] pairs
          }
          
          // Find default template
          const defaultIndex = templates.findIndex(t => t[0].toLowerCase() === 'default');
          if (defaultIndex >= 0) {
            const templateContent = templates[defaultIndex][1];
            setDefaultTemplate(templateContent); // Use the content
            localStorage.setItem(templateCacheKey, templateContent); // Cache it
          }
        } else {
          console.warn("Error getting email templates - API response not successful");
        }
      } catch (error) {
        console.error("Template fetch error:", error);
      }
    };
    fetchDefaultTemplate();

    const fetchMonthlySetting = async () => {
      try {
        let token = props.token;
        if (typeof token === 'function') {
          token = await token();
        }
        if (!token || typeof token !== 'string' || token === 'undefined') {
          return;
        }
        const res = await UsersApi.getMonthlyDistributionSetting(token);
        if (res?.data?.app_metadata?.monthlyDistributionEnabled) {
          setMonthlyEnabled(true);
        }
      } catch (err) {
        // Default to false on error
      }
    };
    fetchMonthlySetting();

    return () => {
      isMounted.current = false;
    };
  }, [props.token, props.user?.sub]); // Add user.sub as dependency to refetch when user changes

  // Reset groupBy to folder when monthly is disabled
  useEffect(() => {
    if (!monthlyEnabled && groupBy === 'month') {
      setGroupBy('folder');
    }
  }, [monthlyEnabled]);

  // Prune empty folders that now have real collections
  useEffect(() => {
    const occupied = new Set();
    collections.forEach((c) => {
      if (Array.isArray(c.folderPath) && c.folderPath.length > 0) {
        occupied.add(JSON.stringify(c.folderPath));
      }
    });
    const pruned = emptyFolders.filter((ef) => !occupied.has(JSON.stringify(ef)));
    if (pruned.length !== emptyFolders.length) {
      setEmptyFolders(pruned);
      saveEmptyFolders(props.user?.sub, pruned);
    }
  }, [collections]);

  // Load empty folders when user changes
  useEffect(() => {
    setEmptyFolders(loadEmptyFolders(props.user?.sub));
    setCurrentPath([]);
  }, [props.user?.sub]);

  // Read ?folder= URL param to restore folder context after navigation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const folderParam = params.get('folder');
    if (folderParam) {
      const path = folderParam.split('/');
      setCurrentPath(path);
    }
  }, []);

  const filteredCollections = useMemo(() => {
    if (collectionFilter === "all") return collections;
    if (collectionFilter === "owned") return collections.filter((c) => !c.isLinked);
    if (collectionFilter === "linked") return collections.filter((c) => c.isLinked === true);
    return collections;
  }, [collections, collectionFilter]);

  const { subfolders, collectionsAtLevel } = useMemo(
    () => buildLevel(filteredCollections, emptyFolders, currentPath),
    [filteredCollections, emptyFolders, currentPath]
  );

  const allFolderPaths = useMemo(() => {
    const paths = new Set();
    collections.forEach((c) => {
      if (Array.isArray(c.folderPath) && c.folderPath.length > 0) {
        paths.add(JSON.stringify(c.folderPath));
      }
    });
    emptyFolders.forEach((ef) => {
      paths.add(JSON.stringify(ef));
    });
    return Array.from(paths).map((s) => JSON.parse(s));
  }, [collections, emptyFolders]);

  const openModal = (currentSet, currentTitle) => {
    if (!showModal) {
      setShowModal(true);
      setCurrentSet(currentSet);
      setCurrentTitle(currentTitle);
      setCurrentEmail("");
    }
  };

  const quickSend = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    let token = props.token;
    if (typeof token === 'function') {
      token = await token();
    }
    if (!token || typeof token !== 'string' || token === 'undefined') {
      toast.error('No valid token found for distribution API call.');
      setModalLoading(false);
      return;
    }
    const result = await Distribute(
      () => token,
      [currentSet], // Send as array of rel_ids, not objects
      currentTitle,
      message,
      [currentEmail]
    );
    setModalLoading(false);
    if (result.success) {
      setShowModal(false);
      toast.success("Success! Please wait a moment for us to process the set...");
    } else {
      toast.error("Something went wrong: Please see console for errors");
    }
  };

  const moveToFolder = async (collectionId, newPath) => {
    setMoveLoading(true);
    try {
      const coll = collections.find((c) => c.collectionId === collectionId || c.relId === collectionId);
      if (!coll) return;
      const currentFp = coll.folderPath || [];
      if (JSON.stringify(currentFp) === JSON.stringify(newPath)) return;
      let token = props.token;
      if (typeof token === 'function') token = await token();
      const result = await CollectionsApi.updateCollectionFolderPath(token, collectionId, newPath);
      if (result.success) {
        toast.success(`Moved "${coll.title}" to ${newPath.length > 0 ? newPath.join(' / ') : 'top level'}`);
        getPhotoListEndpoint();
      } else {
        toast.error("Failed to move: " + (result.message || "Unknown error"));
      }
    } catch (err) {
      toast.error("Failed to move: " + err.message);
    } finally {
      setMoveLoading(false);
    }
  };

  const handleCreateFolder = (newPath) => {
    const pathExists = collections.some((c) => {
      const fp = Array.isArray(c.folderPath) ? c.folderPath : [];
      return JSON.stringify(fp) === JSON.stringify(newPath);
    });
    if (!pathExists) {
      const updated = [...emptyFolders, newPath];
      setEmptyFolders(updated);
      saveEmptyFolders(props.user?.sub, updated);
    }
    setShowNewFolderDialog(false);
  };

  const groupDistribute = async (sets, title) => {
    let successfulDistributions = 0;
    let skipped = 0;

    setDisableButtons(true);

    // Ensure emailList is an array
    const emails = Array.isArray(emailList) ? emailList : [];

    let token = props.token;
    if (typeof token === 'function') {
      token = await token();
    }
    if (!token || typeof token !== 'string' || token === 'undefined') {
      toast.error('No valid token found for distribution API call.');
      setDisableButtons(false);
      return;
    }

    // Process distributions sequentially to avoid relId conflicts
    for (let i = 0; i < sets.length; i++) {
      const value = sets[i];
      
      if (parseInt(value.tier) !== 0) {
        // Include emails from the target tier AND all higher tiers
        // Higher tier numbers mean higher tier levels (e.g., tier 3 > tier 2 > tier 1)
        const targetTier = parseInt(value.tier);
        const tierEmails = emails
          .filter((email) => {
            const emailTier = parseInt(email.tier);
            return emailTier >= targetTier; // Include this tier and all higher tiers
          })
          .map((email) => email.email);
        
        if (process.env.NODE_ENV === 'development') console.log(`Sending ${value.title} (tier ${targetTier}) to ${tierEmails.length} emails from tier ${targetTier} and above`);
        
        try {
          const result = await Distribute(
            () => token,
            [value.relId], // Send as array of rel_ids, not objects
            value.title,
            defaultTemplate || "", // Use default template or empty string
            tierEmails
          );
          
          if (result.success) {
            successfulDistributions++;
            if (process.env.NODE_ENV === 'development') console.log(`✓ Successfully sent ${value.title} (${i + 1}/${sets.length})`);
          } else {
            console.error(`✗ Failed to send ${value.title}:`, result);
            toast.error(`Failed to send ${value.title}: Please see console for errors`);
          }
        } catch (error) {
          console.error(`✗ Exception sending ${value.title}:`, error);
          toast.error(`Failed to send ${value.title}: ${error.message}`);
        }
        
        // Add a small delay between distributions to prevent overwhelming the backend
        if (i < sets.length - 1) { // Don't delay after the last item
          if (process.env.NODE_ENV === 'development') console.log(`Waiting 500ms before next distribution...`);
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
      } else {
        if (process.env.NODE_ENV === 'development') console.log(`Skipping ${value.title} - no tier assigned`);
        toast.warn(`Skipping ${value.title} - no tier assigned`);
        successfulDistributions++;
        skipped++;
      }
    }

    setDisableButtons(false);

    if (successfulDistributions === sets.length && successfulDistributions !== 0) {
      if (successfulDistributions !== skipped)
        toast.success("Sets sent! All sets have been sent successfully.");
      else toast.info("No sets have been sent.");
    } else if (successfulDistributions > 0 && successfulDistributions !== sets.length) {
      toast.warn(`Not all sets have been sent successfully: ${successfulDistributions} out of ${sets.length} sent`);
    } else {
      toast.error("Sending failed: All sets have failed to send.");
    }
  };

  const getPhotoListEndpoint = async () => {
    if (isMounted.current) {
      try {
        let token = props.token;
        if (typeof token === 'function') {
          token = await token();
        }
        const result = await CollectionsApi.getCollections(token);
        
        if (!result.success) {
          toast.error(`Failed to fetch collections: ${result.message}`);
          return;
        }
        
        const responseJson = result.data;

          let list = [];
          let groups = [];
          let groupedSets = [];

          responseJson.tierlist.forEach((item) => {
            ///
            let list_item = {
              title: item.relId,
              //id: item.id,
              relId: item.relId,
              tier: item.tierName,
              color: item.color,
            };

            list.push(list_item);
          });

          let ztier = [];

          list.forEach((list) => {
            let id = list.relId;
            ztier[id] = list;
          });

          if (isMounted.current) {
            setTiers(ztier);
          }

          list = [];

          responseJson.collectionlist.forEach((item) => {
            let tier = parseInt(item.tier);

            // Defensive check for missing tier info - use ztier directly since state may not be updated yet
            let tcolor = "#FFFFFF";
            let tn = "No Tier";
            if (
              ztier &&
              ztier[tier] &&
              typeof ztier[tier].tier !== "undefined"
            ) {
              tcolor = ztier[tier].color;
              tn = ztier[tier].tier;
            }

            let c = tcolor.substring(1); // strip #
            let rgb = parseInt(c, 16); // convert rrggbb to decimal
            let r = (rgb >> 16) & 0xff; // extract red
            let g = (rgb >> 8) & 0xff; // extract green
            let b = (rgb >> 0) & 0xff; // extract blue

            let luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

            let fontColor = "#333333";

            if (luma < 100) fontColor = "#FFFFFF";

            let activeMonth;

            if (item.activeMonth) {
              activeMonth = item.activeMonth;
            } else activeMonth = 0;

            let list_item = {
              title: item.collectionName,
              date: item.createdAt,
              tier: item.tier,
              relId: item.relId,
              setId: item.setId,
              collectionId: item.setId,
              userId: item.userId || null, // owner's userId for linked collections S3 path
              activeMonth: activeMonth,
              folderPath: Array.isArray(item.folderPath) ? item.folderPath : [],
              tierName: tn,
              tierColor: tcolor,
              fontColor: fontColor,
              previewImage: item.previewImage || null, // Use previewImage from backend or null
              isLinked: item.isLinked === true,
              linkType: item.linkType || null,
              linkedAccounts: Array.isArray(item.linkedAccounts) ? item.linkedAccounts : [],
            };

            // Note: We'll store tiers with user-specific key later in the function

            if (!groups.includes("g" + activeMonth)) {
              groups.push("g" + activeMonth);
            }

            list.push(list_item);
          });

          groups.sort().reverse();

          groups.forEach((item) => {
            groupedSets[item] = [];
          });

          list.forEach((item) => {
            groupedSets["g" + item.activeMonth][0] = "g" + item.activeMonth;
            if (!groupedSets["g" + item.activeMonth][1])
              groupedSets["g" + item.activeMonth][1] = [];
            groupedSets["g" + item.activeMonth][1].push(item);
          });

          groupedSets = Object.values(groupedSets);

          if (isMounted.current) {
            setCollections(list);
            
            // Store with user-specific keys
            const userCacheKey = `collections_${props.user?.sub || 'unknown'}`;
            const groupsCacheKey = `groupedSets_${props.user?.sub || 'unknown'}`;
            const tiersCacheKey = `collectionTiers_${props.user?.sub || 'unknown'}`;
            const groupsListCacheKey = `groups_${props.user?.sub || 'unknown'}`;
            
            localStorage.setItem(userCacheKey, JSON.stringify(list));
            localStorage.setItem(groupsCacheKey, JSON.stringify(groupedSets));
            localStorage.setItem(groupsListCacheKey, JSON.stringify(groups));
            localStorage.setItem(tiersCacheKey, JSON.stringify(ztier));
            
            setIsLoading(false);
            setGroups(groups);
            setGroupedSets(groupedSets);
            if (!list.length) setEmpty(true);
          }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.log("Error getting collection list from endpoint", err);
        toast.error("Failed to load collections. Please try again.");
      }
    }
  };

  const handleManageAccess = async (collection) => {
    await refreshUserProfile();
    setSelectedAccessCollection(collection);
    setAccessDialogOpen(true);
  };

  const handleRemoveFromLibrary = async (collection) => {
    const collectionId = collection?.collectionId || collection?.relId;
    if (!collectionId || !props.user?.sub) return;
    try {
      let token = props.token;
      if (typeof token === "function") token = await token();
      const result = await CollectionsApi.unlinkCollection(token, collectionId, props.user.sub);
      if (result.success) {
        toast.success("Collection removed from your library");
        localStorage.removeItem(`collections_${props.user.sub}`);
        getPhotoListEndpoint();
      } else {
        toast.error(result.message || "Failed to remove collection");
      }
    } catch (err) {
      toast.error("Failed to remove collection: " + err.message);
    }
  };

  // Breadcrumb component
  const FolderBreadcrumbs = () => (
    <Box component="nav" aria-label="Folder navigation"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {currentPath.length > 0 && (
          <IconButton size="small" onClick={() => setCurrentPath(currentPath.slice(0, -1))} aria-label="Go up one level">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <IconButton
            size="small"
            onClick={() => setCurrentPath([])}
            aria-label="Go to collections root"
            sx={{ color: currentPath.length === 0 ? 'text.primary' : 'primary.main' }}
          >
            <HomeIcon fontSize="small" />
          </IconButton>
          {currentPath.map((segment, i) => {
            const isLast = i === currentPath.length - 1;
            return isLast ? (
              <Typography key={i} color="text.primary" sx={{ fontWeight: 500 }}>
                {segment}
              </Typography>
            ) : (
              <Button
                key={i}
                variant="text"
                size="small"
                onClick={() => setCurrentPath(currentPath.slice(0, i + 1))}
                sx={{
                  color: 'primary.main',
                  minWidth: 0,
                  textTransform: 'none',
                }}
              >
                {segment}
              </Button>
            );
          })}
        </Breadcrumbs>
      </Box>
      {groupBy === 'folder' && currentPath.length < 2 && (
        <Button
          startIcon={<CreateNewFolderIcon />}
          aria-label="Create new folder"
          onClick={() => setShowNewFolderDialog(true)}
          size="small"
        >
          New Folder
        </Button>
      )}
    </Box>
  );

  // Remove componentWillUnmount and render methods, and refactor the render logic to the function body below

  // Add missing imports for Spinner, Form, Group, Col, Control, FontAwesomeIcon, faPaperPlane, Row if needed

  if (isLoading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', width: '100%' }}>
        <CircularProgress color="primary" />
      </Box>
    );

  return (
    <>
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <Box sx={{ width: 400, margin: "auto", mt: 10, bgcolor: "background.paper", p: 2 }}>
          <Typography variant="h6">Send Set</Typography>
          {modalLoading ? (
            <div className="w-100 px-auto text-center">
              <CircularProgress className="m-4" color="primary" />
            </div>
          ) : (
            <form onSubmit={quickSend}>
              <Grid container spacing={2}>
                <Grid item xs={10}>
                  <TextField
                    type="email"
                    id="modalEmail"
                    label="Email"
                    required
                    onChange={(e) => setCurrentEmail(e.target.value)}
                    value={currentEmail}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={2} className="text-left">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="w-md-75 w-100 mt-2 mt-sm-0"
                  >
                    Send
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <hr />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    multiline
                    rows={3}
                    placeholder="Send a message (optional)"
                    onChange={(e) => setMessage(e.target.value)}
                    value={message}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </Box>
      </Modal>

      <NewFolderDialog
        open={showNewFolderDialog}
        onClose={() => setShowNewFolderDialog(false)}
        onSave={handleCreateFolder}
        existingFolders={allFolderPaths}
        initialParent={currentPath.length === 1 ? currentPath[0] : ""}
      />

      <Box sx={{
        mt: 4,
        pb: { xs: 16, sm: 12, md: 10 } // Bottom padding for footer spacing
      }}>

        {+new Date() < 1600992000000 && (
          <small>
            <Alert severity="info" className="text-left">
              Collections page has been updated. Please check the changelogs
              for notes and information about new features.
              <br />
              <Link to="/updates">Click here for more information </Link>
            </Alert>
          </small>
        )}

        <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          <ControlBar
            Button1="Add Collection"
            ButtonLink={currentPath.length > 0 ? `/collections/add/?folder=${encodeURIComponent(currentPath.join('/'))}` : "/collections/add/"}
            helpHint={
              <HelpHint
                title="Collections"
                hints={[
                  "Create a new collection with the Add Collection button",
                  "Drag and drop collections to reorder them",
                  "Use folders to group related collections",
                ]}
              />
            }
          />
        </Box>

        {!empty && (
          <Box sx={{
            px: { xs: 1, sm: 2, md: 3 },
            mt: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1
          }}>
            {/* Left: Grouping controls */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {monthlyEnabled && (
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="group-by-label">Group by</InputLabel>
                  <Select
                    labelId="group-by-label"
                    label="Group by"
                    value={groupBy}
                    onChange={(e) => { setGroupBy(e.target.value); setCurrentPath([]); }}
                  >
                    <MenuItem value="folder">Group by Folder</MenuItem>
                    <MenuItem value="month">Group by Month</MenuItem>
                    <MenuItem value="none">No Grouping</MenuItem>
                  </Select>
                </FormControl>
              )}
              <ToggleButtonGroup
                value={collectionFilter}
                exclusive
                onChange={(e, value) => {
                  if (value) setCollectionFilter(value);
                }}
                size="small"
                aria-label="Filter collections"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="owned">Owned</ToggleButton>
                <ToggleButton value="linked">Linked</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {/* Right: View mode toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
              size="small"
            >
              <ToggleButton value="thumbnail" aria-label="Thumbnail view"><GridViewIcon /></ToggleButton>
              <ToggleButton value="list" aria-label="List view"><ViewListIcon /></ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {empty ? (
          <Box sx={{
            textAlign: 'center',
            py: { xs: 4, sm: 8 },
            backgroundColor: 'background.paper',
            borderRadius: { xs: 1, sm: 2 },
            border: '2px dashed',
            borderColor: 'divider',
            mt: 3,
            mx: { xs: 0.5, sm: 1, md: 3 }
          }}>
            <Typography variant="h4" color="text.secondary" sx={{ mb: 2, fontWeight: 300 }}>
              No Collections Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              Create your first collection to start organizing and protecting your media.
            </Typography>
            <Button
              component={Link}
              to={currentPath.length > 0 ? `/collections/add/?folder=${encodeURIComponent(currentPath.join('/'))}` : "/collections/add/"}
              variant="contained"
              size="large"
              sx={{ px: 4, py: 1.5 }}
            >
              Create Your First Collection
            </Button>
          </Box>
        ) : (
            <Box sx={{ px: { xs: 0.5, sm: 2, md: 3 }, mt: 3 }}>
              {groupBy === 'folder' && <FolderBreadcrumbs />}

              {groupBy === 'folder' ? (
                <Box>
                  {/* Subfolders */}
                  {subfolders.length > 0 && viewMode === 'thumbnail' && (
                    <Box sx={{ mb: 3 }}>
                      <Grid container spacing={{ xs: 1, sm: 2 }}>
                        {subfolders.map((folder) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={folder.name}>
                            <FolderThumbnail
                              folder={folder}
                              onOpen={() => setCurrentPath(folder.path)}
                              isDropTarget={overFolder === folder.name}
                              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOverFolder(folder.name); }}
                              onDragLeave={() => setOverFolder(null)}
                              onDropCollection={(e) => {
                                e.preventDefault();
                                try {
                                  const d = JSON.parse(e.dataTransfer.getData('text/plain'));
                                  if (d.collectionId) {
                                    moveToFolder(d.collectionId, folder.path);
                                  }
                                } catch {}
                                setOverFolder(null);
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Collections at this level */}
                  {viewMode === 'thumbnail' ? (
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                      {collectionsAtLevel.map((coll) => (
                        <Grid item xs={12} sm={6} md={4} key={coll.relId}>
                          <CollectionListItem
                            data={coll}
                            id={"collection" + coll.relId}
                            AWS={props.AWS}
                            userId={props.user?.sub}
                            openModal={openModal}
                            token={props.token}
                            onMove={(collection) => {
                              setMoveCollection(collection);
                              setMoveDialogOpen(true);
                            }}
                            onRenamed={getPhotoListEndpoint}
                            onDeleted={getPhotoListEndpoint}
                            accountType={accountType}
                            onManageAccess={handleManageAccess}
                            onRemoveFromLibrary={handleRemoveFromLibrary}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <CollectionListView
                      collections={collectionsAtLevel}
                      subfolders={subfolders}
                      onFolderOpen={(path) => setCurrentPath(path)}
                      AWS={props.AWS}
                      userId={props.user?.sub}
                      openModal={openModal}
                      token={props.token}
                      onMove={(collection) => {
                        setMoveCollection(collection);
                        setMoveDialogOpen(true);
                      }}
                      onRenamed={getPhotoListEndpoint}
                      onDeleted={getPhotoListEndpoint}
                      onDropIntoFolder={moveToFolder}
                      accountType={accountType}
                      onManageAccess={handleManageAccess}
                      onRemoveFromLibrary={handleRemoveFromLibrary}
                    />
                  )}

                  {/* Empty state */}
                  {subfolders.length === 0 && collectionsAtLevel.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <FolderIcon sx={{ fontSize: 48, mb: 1 }} />
                      <Typography>This folder is empty. Move collections here or create a subfolder.</Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                /* Month/None: flatten all collections, no folders */
                <Box>
                  {groupBy === 'month' ? (
                    groupedSets.map((item, index) => {
                      const groupLabel = item[0];
                      return (
                        <Paper key={groupLabel || index} sx={{ mb: { xs: 2, sm: 4 }, mx: { xs: 0.5, sm: 0 }, p: { xs: 1.5, sm: 3 }, borderRadius: { xs: 1, sm: 2 }, boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 8px', bgcolor: 'background.paper' }}>
                          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                            {Moment.unix(parseInt(groupLabel.replace('g', ''))).format('MMMM YYYY')}
                          </Typography>
                          {viewMode === 'thumbnail' ? (
                            <Grid container spacing={{ xs: 1, sm: 2 }}>
                              {item[1].map((coll) => (
                                <Grid item xs={12} sm={6} md={4} key={coll.relId}>
                                <CollectionListItem
                                  data={coll}
                                  id={"collection" + coll.relId}
                                  AWS={props.AWS}
                                  userId={props.user?.sub}
                                  openModal={openModal}
                                  token={props.token}
                                  onMove={(collection) => {
                                    setMoveCollection(collection);
                                    setMoveDialogOpen(true);
                                  }}
                                  onRenamed={getPhotoListEndpoint}
                                  onDeleted={getPhotoListEndpoint}
                                  accountType={accountType}
                                  onManageAccess={handleManageAccess}
                                  onRemoveFromLibrary={handleRemoveFromLibrary}
                                />
                                </Grid>
                              ))}
                            </Grid>
                          ) : (
                            <CollectionListView
                              collections={item[1]}
                              AWS={props.AWS}
                              userId={props.user?.sub}
                              openModal={openModal}
                              token={props.token}
                              onMove={(collection) => {
                                setMoveCollection(collection);
                                setMoveDialogOpen(true);
                              }}
                              onRenamed={getPhotoListEndpoint}
                              onDeleted={getPhotoListEndpoint}
                              onDropIntoFolder={moveToFolder}
                              accountType={accountType}
                              onManageAccess={handleManageAccess}
                              onRemoveFromLibrary={handleRemoveFromLibrary}
                            />
                          )}
                        </Paper>
                      );
                    })
                  ) : (
                    /* No grouping */
                    viewMode === 'thumbnail' ? (
                      <Grid container spacing={{ xs: 1, sm: 2 }}>
                        {collections.map((coll) => (
                          <Grid item xs={12} sm={6} md={4} key={coll.relId}>
                            <CollectionListItem
                              data={coll}
                              id={"collection" + coll.relId}
                              AWS={props.AWS}
                              userId={props.user?.sub}
                              openModal={openModal}
                              token={props.token}
                              onMove={(collection) => {
                                setMoveCollection(collection);
                                setMoveDialogOpen(true);
                              }}
                              onRenamed={getPhotoListEndpoint}
                              onDeleted={getPhotoListEndpoint}
                            accountType={accountType}
                            onManageAccess={handleManageAccess}
                            onRemoveFromLibrary={handleRemoveFromLibrary}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <CollectionListView
                        collections={collections}
                        AWS={props.AWS}
                        userId={props.user?.sub}
                        openModal={openModal}
                        token={props.token}
                        onMove={(collection) => {
                          setMoveCollection(collection);
                          setMoveDialogOpen(true);
                        }}
                        onRenamed={getPhotoListEndpoint}
                        onDeleted={getPhotoListEndpoint}
                        onDropIntoFolder={moveToFolder}
                      />
                    )
                  )}
                </Box>
              )}
            </Box>
        )}
      </Box>
      <CollectionFolderMoveDialog
        open={moveDialogOpen}
        onClose={() => { setMoveDialogOpen(false); setMoveCollection(null); }}
        collectionId={moveCollection?.collectionId || moveCollection?.relId}
        currentFolderPath={moveCollection?.folderPath || []}
        collections={collections}
        emptyFolders={emptyFolders}
        token={props.token}
        onMoved={() => {
          setMoveDialogOpen(false);
          setMoveCollection(null);
          toast.success("Collection moved successfully");
          getPhotoListEndpoint();
        }}
      />
      <CollectionAccessDialog
        open={accessDialogOpen}
        onClose={() => {
          setAccessDialogOpen(false);
          setSelectedAccessCollection(null);
        }}
        collection={selectedAccessCollection}
        activeLinks={(props.user?.linkedAccountIds || props.user?.app_metadata?.linkedAccountIds || []).filter(
          (entry) => entry?.status === "active"
        )}
        token={props.token}
        onUpdated={() => {
          localStorage.removeItem(`collections_${props.user.sub}`);
          getPhotoListEndpoint();
        }}
      />
      <Backdrop open={moveLoading} sx={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}

export default Collections;
